import { useLocation } from "react-router-dom";
import { Icon } from "../icons/Icon.tsx";
import { TopbarTournamentSwitcher } from "../intl-acca/TopbarTournamentSwitcher.tsx";
import { useIntlAccaTournaments } from "../../intl-acca/useIntlAccaTournaments.ts";
import { SportSwitcher } from "./SportSwitcher.tsx";

interface Crumb {
  label: string;
  active?: boolean;
}

const ROUTE_CRUMBS: Record<string, Crumb[]> = {
  "/home": [{ label: "Workspace" }, { label: "Home", active: true }],
  "/exchange": [
    { label: "Workspace" },
    { label: "Markets" },
    { label: "The Exchange", active: true },
  ],
  "/singles": [
    { label: "Workspace" },
    { label: "Daily" },
    { label: "Singles Chain", active: true },
  ],
  "/rollover": [{ label: "Workspace" }, { label: "Plans" }, { label: "ROLLOVER", active: true }],
  "/streaks": [{ label: "Workspace" }, { label: "Daily" }, { label: "STREAKS", active: true }],
  "/powerball": [{ label: "Workspace" }, { label: "Daily" }, { label: "POWERBALL", active: true }],
  "/ladder": [{ label: "Workspace" }, { label: "Weekly" }, { label: "LADDER", active: true }],
  "/owl": [{ label: "Workspace" }, { label: "Late Night" }, { label: "OWL HOUR", active: true }],
  "/fair-price": [
    { label: "Workspace" },
    { label: "Intelligence" },
    { label: "Fair Price", active: true },
  ],
  "/sika": [{ label: "Workspace" }, { label: "Sika", active: true }],
  "/vault": [{ label: "Workspace" }, { label: "Vault", active: true }],
  "/watchlist": [{ label: "Workspace" }, { label: "Watchlist", active: true }],
  "/brief": [{ label: "Workspace" }, { label: "The Brief", active: true }],
  "/council": [{ label: "Workspace" }, { label: "The Council", active: true }],
  "/pitch": [{ label: "Workspace" }, { label: "The Pitch", active: true }],
  "/reports": [{ label: "Workspace" }, { label: "Tools" }, { label: "Reports", active: true }],
  "/audit": [{ label: "Workspace" }, { label: "Tools" }, { label: "Audit Log", active: true }],
  "/knowledge": [{ label: "Workspace" }, { label: "Tools" }, { label: "Knowledge", active: true }],
  "/boards": [{ label: "Workspace" }, { label: "The Board", active: true }],
  "/intelligence": [{ label: "Workspace" }, { label: "Tools" }, { label: "Intelligence", active: true }],
  "/agents": [{ label: "Workspace" }, { label: "Tools" }, { label: "Agents", active: true }],
  "/trust": [{ label: "Workspace" }, { label: "Tools" }, { label: "Trust Center", active: true }],
  "/dashboard": [{ label: "Workspace" }, { label: "Dashboard", active: true }],
  "/admin": [{ label: "Workspace" }, { label: "Tools" }, { label: "Admin Dashboard", active: true }],
  "/templates": [{ label: "Workspace" }, { label: "Tools" }, { label: "Templates", active: true }],
  "/academy": [{ label: "Workspace" }, { label: "Tools" }, { label: "Academy", active: true }],
};

interface TopbarProps {
  modePill?: { label: string; live?: boolean };
  actions?: JSX.Element;
  leftSlot?: JSX.Element;
}

export function Topbar({ modePill, actions, leftSlot }: TopbarProps): JSX.Element {
  const location = useLocation();
  const crumbs = ROUTE_CRUMBS[location.pathname] ?? [
    { label: "Workspace" },
    { label: "Home", active: true },
  ];

  const pill = modePill ?? { label: "Live · Safe Mode", live: false };

  return (
    <header className="topbar">
      {leftSlot}
      {/* Hero sport switcher — always visible. Swaps the whole workspace
          (dashboard + sidebar) between live sports. */}
      <SportSwitcher />
      {/* Tournament switcher — only on /intl-acca/* routes. The hook
          (and its fetch) lives inside IntlAccaSwitcher so it never
          runs outside the Intl Acca section. INTEGRATION_PATCH §3 —
          reconciled: the real Topbar has no `.topbar-left`/<Logo/>
          cluster, so it mounts in the existing left region (after
          leftSlot, before the crumbs). */}
      {location.pathname.startsWith("/intl-acca/") ? <IntlAccaSwitcher /> : null}
      <div className="crumbs">
        {crumbs.map((c, i) => (
          <span key={i}>
            {i > 0 && <span className="sep"> / </span>}
            <span className={c.active ? "here" : ""}>{c.label}</span>
          </span>
        ))}
      </div>

      <div className="tb-actions">
        <div className={`mode-pill${pill.live ? " live" : ""}`}>
          <span className="dot" />
          {pill.label}
        </div>
        <button type="button" className="icon-btn" title="Refresh">
          <Icon name="refresh" size={16} />
        </button>
        <button type="button" className="icon-btn" title="Notifications">
          <Icon name="bell" size={16} />
        </button>
        {actions}
      </div>
    </header>
  );
}

function IntlAccaSwitcher(): JSX.Element | null {
  const { tournaments, activeId, setActive, navigate } = useIntlAccaTournaments();
  return (
    <TopbarTournamentSwitcher
      tournaments={tournaments}
      activeTournamentId={activeId}
      onSelect={(id) => {
        setActive(id);
        navigate(`/intl-acca/${id}`);
      }}
      onOpenCalendar={() => navigate("/intl-acca/calendar")}
    />
  );
}
